"use client";

import React, { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from "react";
import { doc, collection, onSnapshot, setDoc, deleteDoc, serverTimestamp, Unsubscribe, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useTenancy } from "@/contexts/TenancyContext";
import type { OnboardingStepKey } from "./steps";
import { STARTER_ONBOARDING_STEPS } from "./steps";

export type StepStatus = "todo" | "in_progress" | "done";

/** 
 * Data stored per step document in Firestore
 * Path: customers/{customerID}/onboarding/steps/items/{stepKey}
 */
export interface StepDocument {
  status: StepStatus;
  /** Form data entered by the user - varies per step type */
  data?: Record<string, any>;
  /** When the step was started */
  startedAt?: any;
  /** When the step was last updated */
  updatedAt?: any;
  /** When the step was completed */
  completedAt?: any;
  /** User ID who last updated */
  updatedBy?: string;
}

/**
 * Active user presence for a step
 * Path: customers/{customerID}/onboardingPresence/{visitorId}
 */
export interface StepPresence {
  visitorId: string;
  userId: string;
  userName: string;
  stepKey: OnboardingStepKey;
  /** Which field the user is currently focused on */
  activeField?: string;
  lastSeen: Timestamp;
}

/** Map of stepKey -> array of active users */
export type PresenceMap = Record<OnboardingStepKey, StepPresence[]>;

/** Map of fieldName -> users currently editing it */
export type FieldPresenceMap = Record<string, StepPresence[]>;

interface OnboardingState {
  /** Status of each step */
  statuses: Record<OnboardingStepKey, StepStatus>;
  /** Full step data including form values */
  stepData: Record<OnboardingStepKey, StepDocument>;
  /** Active users per step (real-time presence) */
  presence: PresenceMap;
  /** Update step status */
  setStatus: (key: OnboardingStepKey, status: StepStatus) => void;
  /** Update step form data (persisted in real-time) */
  setStepData: (key: OnboardingStepKey, data: Record<string, any>) => void;
  /** Get step form data */
  getStepData: (key: OnboardingStepKey) => Record<string, any> | undefined;
  /** Set which field the current user is focused on */
  setActiveField: (fieldName: string | null) => void;
  /** Get users editing fields in the current step */
  getFieldPresence: (stepKey: OnboardingStepKey) => FieldPresenceMap;
  /** Check if step dependencies are met */
  canComplete: (key: OnboardingStepKey) => boolean;
  /** Open a step (sets it to in_progress if not done) */
  openStep: (key: OnboardingStepKey) => void;
  /** Currently active step */
  activeStep: OnboardingStepKey | null;
  /** Close the active step modal */
  closeActive: () => void;
  /** Go back to the previous step in the flow */
  goBack: () => void;
  /** Get the previous step key for a given step */
  getPreviousStep: (key: OnboardingStepKey) => OnboardingStepKey | null;
  /** Whether data is still loading from Firestore */
  loading: boolean;
  /** Any error that occurred during sync */
  error: Error | null;
}

const OnboardingContext = createContext<OnboardingState | null>(null);

/** Build default statuses with all steps as "todo" */
const getDefaultStatuses = (): Record<OnboardingStepKey, StepStatus> => {
  return STARTER_ONBOARDING_STEPS.reduce((acc, s) => {
    acc[s.key] = "todo";
    return acc;
  }, {} as Record<OnboardingStepKey, StepStatus>);
};

/** Build empty step data record */
const getDefaultStepData = (): Record<OnboardingStepKey, StepDocument> => {
  return STARTER_ONBOARDING_STEPS.reduce((acc, s) => {
    acc[s.key] = { status: "todo" };
    return acc;
  }, {} as Record<OnboardingStepKey, StepDocument>);
};

/** Build empty presence map */
const getDefaultPresence = (): PresenceMap => {
  return STARTER_ONBOARDING_STEPS.reduce((acc, s) => {
    acc[s.key] = [];
    return acc;
  }, {} as PresenceMap);
};

/** Generate a unique visitor ID for this browser tab */
const getVisitorId = (): string => {
  if (typeof window === 'undefined') return 'ssr';
  let id = sessionStorage.getItem('onboarding_visitor_id');
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem('onboarding_visitor_id', id);
  }
  return id;
};

/** Presence is stale after 10 seconds without heartbeat */
const PRESENCE_STALE_MS = 10_000;
/** Heartbeat interval */
const HEARTBEAT_INTERVAL_MS = 5_000;

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { activeTenancy, currentUser, userIdToName } = useTenancy();
  const tenancyId = activeTenancy?.id;
  const userId = currentUser?.userID;
  const userName = userId ? (userIdToName[userId] || 'Unknown') : 'Unknown';
  
  const [statuses, setStatuses] = useState<Record<OnboardingStepKey, StepStatus>>(getDefaultStatuses);
  const [stepData, setStepDataState] = useState<Record<OnboardingStepKey, StepDocument>>(getDefaultStepData);
  const [presence, setPresence] = useState<PresenceMap>(getDefaultPresence);
  const [activeStep, setActiveStep] = useState<OnboardingStepKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Unique ID for this browser tab
  const visitorIdRef = useRef<string>(getVisitorId());
  
  // Track our own writes to prevent echo updates (timestamp per step)
  const lastWriteRef = useRef<Partial<Record<OnboardingStepKey, number>>>({});
  const unsubscribesRef = useRef<Map<OnboardingStepKey, Unsubscribe>>(new Map());
  const presenceUnsubRef = useRef<Unsubscribe | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  // Get document reference for a specific step
  const getStepDocRef = useCallback((stepKey: OnboardingStepKey) => {
    if (!tenancyId || tenancyId === "Loading...") return null;
    return doc(db, "customers", tenancyId, "onboarding", "steps", "items", stepKey);
  }, [tenancyId]);

  // Get presence document reference for this visitor
  // Path: customers/{customerID}/onboardingPresence/{visitorId}
  const getPresenceDocRef = useCallback(() => {
    if (!tenancyId || tenancyId === "Loading...") return null;
    return doc(db, "customers", tenancyId, "onboardingPresence", visitorIdRef.current);
  }, [tenancyId]);

  // Track which field the user is currently focused on
  const activeFieldRef = useRef<string | null>(null);

  // Update presence in Firestore (called on step open, field focus, and heartbeat)
  const updatePresence = useCallback(async (stepKey: OnboardingStepKey | null, activeField?: string | null) => {
    const presenceRef = getPresenceDocRef();
    if (!presenceRef) return;

    try {
      if (stepKey) {
        await setDoc(presenceRef, {
          visitorId: visitorIdRef.current,
          userId: userId || 'unknown',
          userName,
          stepKey,
          activeField: activeField ?? activeFieldRef.current ?? null,
          lastSeen: serverTimestamp(),
        });
      } else {
        // Clear presence when leaving
        await deleteDoc(presenceRef);
      }
    } catch (err) {
      console.error("[OnboardingContext] Failed to update presence:", err);
    }
  }, [getPresenceDocRef, userId, userName]);

  // Set which field the user is currently editing
  const setActiveField = useCallback((fieldName: string | null) => {
    activeFieldRef.current = fieldName;
    if (activeStep) {
      updatePresence(activeStep, fieldName);
    }
  }, [activeStep, updatePresence]);

  // Get field presence for a step (which users are editing which fields)
  const getFieldPresence = useCallback((stepKey: OnboardingStepKey): FieldPresenceMap => {
    const users = presence[stepKey] || [];
    const fieldMap: FieldPresenceMap = {};
    for (const user of users) {
      if (user.activeField) {
        if (!fieldMap[user.activeField]) {
          fieldMap[user.activeField] = [];
        }
        fieldMap[user.activeField].push(user);
      }
    }
    return fieldMap;
  }, [presence]);

  // Subscribe to presence collection
  useEffect(() => {
    if (!tenancyId || tenancyId === "Loading...") return;

    // Path: customers/{customerID}/onboardingPresence
    const presenceCollectionRef = collection(db, "customers", tenancyId, "onboardingPresence");
    
    presenceUnsubRef.current = onSnapshot(
      presenceCollectionRef,
      (snapshot) => {
        const now = Date.now();
        const newPresence = getDefaultPresence();
        
        snapshot.docs.forEach((doc) => {
          const data = doc.data() as StepPresence;
          const stepKey = data.stepKey as OnboardingStepKey;
          
          // Check if presence is stale
          const lastSeen = data.lastSeen?.toMillis?.() || 0;
          if (now - lastSeen > PRESENCE_STALE_MS) return;
          
          // Don't show self in presence
          if (data.visitorId === visitorIdRef.current) return;
          
          if (newPresence[stepKey]) {
            newPresence[stepKey].push(data);
          }
        });
        
        setPresence(newPresence);
      },
      (err) => {
        console.error("[OnboardingContext] Presence subscription error:", err);
      }
    );

    return () => {
      if (presenceUnsubRef.current) {
        presenceUnsubRef.current();
        presenceUnsubRef.current = null;
      }
    };
  }, [tenancyId]);

  // Heartbeat to keep presence alive + cleanup on unmount
  useEffect(() => {
    if (!activeStep) {
      // Clear presence when no step is active
      updatePresence(null);
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    // Set initial presence
    updatePresence(activeStep);

    // Start heartbeat
    heartbeatRef.current = setInterval(() => {
      updatePresence(activeStep);
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [activeStep, updatePresence]);

  // Cleanup presence on page unload
  useEffect(() => {
    const cleanup = () => {
      const presenceRef = getPresenceDocRef();
      if (presenceRef) {
        // Use sendBeacon for reliable cleanup on page close
        deleteDoc(presenceRef).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', cleanup);
    window.addEventListener('pagehide', cleanup);
    
    return () => {
      window.removeEventListener('beforeunload', cleanup);
      window.removeEventListener('pagehide', cleanup);
      cleanup();
    };
  }, [getPresenceDocRef]);

  // Subscribe to all step documents
  useEffect(() => {
    if (!tenancyId || tenancyId === "Loading...") {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Subscribe to each step document individually for granular updates
    const newStatuses = { ...getDefaultStatuses() };
    const newStepData = { ...getDefaultStepData() };
    let loadedCount = 0;
    const totalSteps = STARTER_ONBOARDING_STEPS.length;

    STARTER_ONBOARDING_STEPS.forEach((step) => {
      const docRef = doc(db, "customers", tenancyId, "onboarding", "steps", "items", step.key);
      
      const unsubscribe = onSnapshot(
        docRef,
        (snapshot) => {
          // Skip echo updates from our own writes (within 500ms)
          const lastWrite = lastWriteRef.current[step.key];
          const now = Date.now();
          if (lastWrite && now - lastWrite < 500) {
            // Still allow initial load
            if (loadedCount < totalSteps) {
              loadedCount++;
              if (loadedCount >= totalSteps) {
                setLoading(false);
              }
            }
            return;
          }
          
          if (snapshot.exists()) {
            const data = snapshot.data() as StepDocument;
            newStatuses[step.key] = data.status || "todo";
            newStepData[step.key] = data;
          } else {
            newStatuses[step.key] = "todo";
            newStepData[step.key] = { status: "todo" };
          }
          
          // Update state
          setStatuses(prev => ({ ...prev, [step.key]: newStatuses[step.key] }));
          setStepDataState(prev => ({ ...prev, [step.key]: newStepData[step.key] }));
          
          loadedCount++;
          if (loadedCount >= totalSteps) {
            setLoading(false);
          }
        },
        (err) => {
          console.error(`[OnboardingContext] Error subscribing to step ${step.key}:`, err);
          setError(err);
          loadedCount++;
          if (loadedCount >= totalSteps) {
            setLoading(false);
          }
        }
      );
      
      unsubscribesRef.current.set(step.key, unsubscribe);
    });

    return () => {
      unsubscribesRef.current.forEach((unsub) => unsub());
      unsubscribesRef.current.clear();
    };
  }, [tenancyId]);

  // Persist step document to Firestore
  const persistStep = useCallback(async (
    key: OnboardingStepKey, 
    updates: Partial<StepDocument>
  ) => {
    const docRef = getStepDocRef(key);
    if (!docRef) return;

    // Mark that we're writing this step (to prevent echo updates)
    lastWriteRef.current[key] = Date.now();
    
    try {
      const currentData = stepData[key] || { status: "todo" };
      const isNewStart = !currentData.startedAt && updates.status === "in_progress";
      const isCompletion = updates.status === "done" && currentData.status !== "done";
      
      // Merge nested data fields properly - ensure we never pass undefined to Firebase
      const mergedData = updates.data 
        ? { ...(currentData.data || {}), ...updates.data }
        : currentData.data;
      
      await setDoc(docRef, {
        status: updates.status ?? currentData.status ?? "todo",
        ...(mergedData ? { data: mergedData } : {}),
        updatedAt: serverTimestamp(),
        updatedBy: userId || "unknown",
        ...(currentData.startedAt ? { startedAt: currentData.startedAt } : {}),
        ...(isNewStart ? { startedAt: serverTimestamp() } : {}),
        ...(currentData.completedAt ? { completedAt: currentData.completedAt } : {}),
        ...(isCompletion ? { completedAt: serverTimestamp() } : {}),
      });
      
    } catch (err) {
      console.error(`[OnboardingContext] Failed to persist step ${key}:`, err);
      setError(err as Error);
    }
  }, [getStepDocRef, userId, stepData]);

  // Update step status
  const setStatus = useCallback((key: OnboardingStepKey, status: StepStatus) => {
    // Optimistic update
    setStatuses(prev => ({ ...prev, [key]: status }));
    setStepDataState(prev => ({ 
      ...prev, 
      [key]: { ...prev[key], status } 
    }));
    // Persist
    persistStep(key, { status });
  }, [persistStep]);

  // Update step form data (for real-time persistence)
  const setStepData = useCallback((key: OnboardingStepKey, data: Record<string, any>) => {
    // Optimistic update
    setStepDataState(prev => ({
      ...prev,
      [key]: { ...prev[key], data: { ...prev[key]?.data, ...data } }
    }));
    // Persist
    persistStep(key, { data });
  }, [persistStep]);

  // Get step form data
  const getStepData = useCallback((key: OnboardingStepKey): Record<string, any> | undefined => {
    return stepData[key]?.data;
  }, [stepData]);

  const dependsMap = useMemo(() => {
    return STARTER_ONBOARDING_STEPS.reduce((acc, s) => {
      acc[s.key] = s.dependsOn || [];
      return acc;
    }, {} as Record<OnboardingStepKey, OnboardingStepKey[]>);
  }, []);

  const canComplete = useCallback((key: OnboardingStepKey) => {
    const deps = dependsMap[key] || [];
    return deps.every(d => statuses[d] === "done");
  }, [dependsMap, statuses]);

  const openStep = useCallback((key: OnboardingStepKey) => {
    if (statuses[key] !== "done") {
      setStatus(key, "in_progress");
    }
    setActiveStep(key);
  }, [statuses, setStatus]);

  const closeActive = useCallback(() => setActiveStep(null), []);

  // Get the previous step in the flow based on array order
  const getPreviousStep = useCallback((key: OnboardingStepKey): OnboardingStepKey | null => {
    const currentIndex = STARTER_ONBOARDING_STEPS.findIndex(s => s.key === key);
    if (currentIndex <= 0) return null;
    return STARTER_ONBOARDING_STEPS[currentIndex - 1].key;
  }, []);

  // Go back to the previous step
  const goBack = useCallback(() => {
    if (!activeStep) {
      setActiveStep(null);
      return;
    }
    const prevStep = getPreviousStep(activeStep);
    if (prevStep) {
      openStep(prevStep);
    } else {
      setActiveStep(null);
    }
  }, [activeStep, getPreviousStep, openStep]);

  const value: OnboardingState = {
    statuses,
    stepData,
    presence,
    setStatus,
    setStepData,
    getStepData,
    setActiveField,
    getFieldPresence,
    canComplete,
    openStep,
    activeStep,
    closeActive,
    goBack,
    getPreviousStep,
    loading,
    error,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

// Default no-op implementation for when used outside provider
const defaultOnboardingState: OnboardingState = {
  statuses: {} as Record<OnboardingStepKey, StepStatus>,
  stepData: {} as Record<OnboardingStepKey, StepDocument>,
  presence: {} as PresenceMap,
  setStatus: () => {},
  setStepData: () => {},
  getStepData: () => undefined,
  setActiveField: () => {},
  getFieldPresence: () => ({}),
  canComplete: () => true,
  openStep: () => {},
  activeStep: null,
  closeActive: () => {},
  goBack: () => {},
  getPreviousStep: () => null,
  loading: false,
  error: null,
};

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    console.warn("useOnboarding used outside OnboardingProvider - using no-op fallback");
    return defaultOnboardingState;
  }
  return ctx;
}
